/**
 * Alshallal Pastry Company HR System - Language Context
 * Manages bilingual support (English/Arabic) with RTL
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Translations
const translations = {
  en: {
    // Common
    app_name: 'Alshallal HR',
    app_tagline: 'Human Resources Management',
    loading: 'Loading...',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    search: 'Search',
    filter: 'Filter',
    export: 'Export',
    actions: 'Actions',
    status: 'Status',
    view: 'View',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    submit: 'Submit',
    yes: 'Yes',
    no: 'No',
    
    // Auth
    login: 'Login',
    logout: 'Logout',
    email: 'Email',
    password: 'Password',
    forgot_password: 'Forgot Password?',
    reset_password: 'Reset Password',
    change_password: 'Change Password',
    current_password: 'Current Password',
    new_password: 'New Password',
    confirm_password: 'Confirm Password',
    identifier_placeholder: 'Email, National ID, or Iqama',
    login_welcome: 'Welcome to Alshallal HR',
    login_subtitle: 'Sign in to access your HR portal',
    first_login_notice: 'Please change your password to continue',
    
    // Navigation
    dashboard: 'Dashboard',
    employees: 'Employees',
    users: 'Users',
    documents: 'Documents',
    leave: 'Leave',
    requests: 'Requests',
    reports: 'Reports',
    settings: 'Settings',
    audit_logs: 'Audit Logs',
    system: 'System',
    profile: 'Profile',
    
    // Roles
    role_employee: 'Employee',
    role_manager: 'Manager',
    role_hr_assistant: 'HR Assistant',
    role_hr_manager: 'HR Manager',
    role_hr_admin: 'HR Admin',
    role_it_inf: 'IT INF',
    role_super_admin: 'Super Admin',
    
    // Dashboard
    welcome_back: 'Welcome back',
    signed_out_idle: 'You were signed out because the session was idle. Please sign in again.',
    signed_out_revoked: 'This session was signed out. Please sign in again.',
    signed_out_locked: 'This account is locked. Please contact HR.',
    signed_out_inactive: 'This account has been deactivated. Please contact HR.',
    quick_actions: 'Quick Actions',
    recent_activity: 'Recent Activity',
    pending_approvals: 'Pending Approvals',
    no_pending_items: 'No pending items',
    
    // Audit
    audit_action: 'Action',
    audit_actor: 'Performed By',
    audit_target: 'Target',
    audit_timestamp: 'Timestamp',
    audit_reason: 'Reason',
    audit_details: 'Details',
    
    // Users
    user_management: 'User Management',
    create_user: 'Create User',
    user_details: 'User Details',
    first_name: 'First Name',
    last_name: 'Last Name',
    national_id: 'National ID',
    iqama: 'Iqama',
    role: 'Role',
    active: 'Active',
    inactive: 'Inactive',
    
    // System
    system_health: 'System Health',
    diagnostics: 'Diagnostics',
    email_status: 'Email Status',
    database_status: 'Database Status',
    
    // Errors
    error_occurred: 'An error occurred',
    error_auth_required: 'Authentication required',
    error_access_denied: 'Access denied',
    error_not_found: 'Not found',
    error_validation: 'Validation error',
    
    // Success messages
    success_saved: 'Saved successfully',
    success_deleted: 'Deleted successfully',
    success_updated: 'Updated successfully',
    
    // Empty states
    no_data: 'No data available',
    no_results: 'No results found',
    get_started: 'Get Started',
    
    // Import (Phase 13)
    bulkEmployeeImport: 'Bulk Employee Import',
    importDescription: 'Import employees from Excel or CSV files',
    uploadImport: 'Upload & Import',
    importHistory: 'Import History',
    approvalsAudit: 'Approvals Audit',
    approvalsAuditHint: 'Who approved which file (by SHA-256), when, and whether it was executed.',
    bulkSalary: 'Bulk Salary',
    bulkSalaryLoader: 'Bulk Basic-Salary Loader',
    bulkSalaryLoaderHint: 'Reads only the identity columns (Iqama / National ID / Employee Number) and the Salary column, and writes only Basic Salary. Matching priority: Iqama → National ID → Employee Number. Empty/0 is set from the file; a stored salary that differs is flagged as a CONFLICT and never overwritten.',
    salaryReasonNote: 'Every salary write is recorded with the protected-field reason:',
    salarySelectFile: 'Select a CSV/XLSX file with identity columns and a Salary column.',
    salaryRunDryRun: 'Run Dry-Run',
    salaryDryRunFailed: 'Dry-run failed.',
    salaryExecuteFailed: 'Execute failed.',
    salaryApprovalRequired: 'Run a dry-run that has at least one SET row to approve this file before executing.',
    salaryExecuted: 'Executed.',
    salaryDryRunReport: 'Dry-Run Preview (no changes written)',
    salaryWillSet: 'Will Set',
    salaryNoChange: 'No Change',
    salaryConflict: 'Conflict',
    salarySkip: 'Skipped',
    salaryNotFound: 'Not Found',
    salaryFailed: 'Failed',
    salaryReady: 'Ready to execute — an approval token is bound to this file.',
    salaryNotReady: 'Nothing to set (no empty/0 salaries matched). Execution is not enabled.',
    salaryCurrent: 'Current',
    salaryFromFile: 'From File',
    salaryExecute: 'Execute Salary Load',
    salaryLoadHistory: 'Salary Load History',
    salaryLoadHistoryHint: 'Who loaded which file (by SHA-256), when, status. Download the number-free sign-off.',
    noSalaryBatches: 'No salary loads yet. A dry-run records a batch here.',
    matchedBy: 'Matched By',
    uploadedBy: 'By',
    noApprovals: 'No approvals yet. A passing dry-run records an approval here.',
    fileSha: 'File SHA-256',
    approvedBy: 'Approved by',
    approvedAt: 'Approved at',
    executedBy: 'Executed by',
    executed: 'Executed',
    pendingExecution: 'Pending',
    approvalRequired: 'Run a dry-run that passes to approve this file before executing.',
    signoffSheet: 'Sign-off',
    signoffDownloadFailed: 'Could not download the sign-off sheet.',
    uploadFile: 'Upload File',
    supportedFormats: 'Supported formats: .xlsx, .csv',
    importProfile: 'Import Profile',
    selectProfile: 'Select profile',
    selectFile: 'Select File',
    selectSheet: 'Select Sheet',
    sheetHint: 'Change sheet and re-validate to use different data',
    validating: 'Validating...',
    validateFile: 'Validate File',
    validationResult: 'Validation Result',
    uploadToValidate: 'Upload a file to see validation results',
    importComplete: 'Import completed successfully!',
    created: 'Created',
    updated: 'Updated',
    noChange: 'No Change',
    failed: 'Failed',
    downloadCredentials: 'Download Credentials CSV',
    totalRows: 'Total Rows',
    total: 'Total',
    warnings: 'Warnings',
    rowsKept: 'rows kept, not rejected',
    note: 'Note',
    runDryRun: 'Run Dry-Run Preview',
    runningDryRun: 'Running dry-run...',
    dryRunHint: 'Preview the match/upsert outcome below. Nothing is written until you Execute.',
    dryRunPreview: 'Dry-Run Preview',
    noDataWritten: 'no data written',
    upsertSummary: 'Match priority Iqama → National ID → Employee #. Non-identity fields overwrite; identity fields written only via the matched key. Idempotent, no deletes.',
    willCreate: 'Create',
    willUpdate: 'Update',
    willFail: 'Fail',
    blocked: 'Blocked',
    readyToExecute: 'No blocking issues — ready to execute.',
    blockedFromExecute: 'Blocking issues must be resolved before Execute (see flags below).',
    flagDetail: 'Flag Detail',
    noValuesShown: 'row & field only, never values',
    severity: 'Severity',
    type: 'Type',
    resolveBlocking: 'Resolve blocking flags, re-validate, then re-run the dry-run before executing.',
    valid: 'Valid',
    invalid: 'Invalid',
    file: 'File',
    sheet: 'Sheet',
    blockingErrors: 'Blocking Errors',
    rowErrors: 'Row Errors',
    row: 'Row',
    field: 'Field',
    error: 'Error',
    executing: 'Executing...',
    executeImport: 'Execute Import',
    fixErrorsFirst: 'Fix validation errors before executing',
    refresh: 'Refresh',
    noImportHistory: 'No import history yet',
    rows: 'Rows',
    result: 'Result',
    date: 'Date',
    clickToUpload: 'Click to upload or drag and drop',
    noValidationId: 'No validation ID - please validate first',
    pleaseSelectFileAndProfile: 'Please select a file and profile',
  },
  ar: {
    // Common
    app_name: 'الشلال للموارد البشرية',
    app_tagline: 'إدارة الموارد البشرية',
    loading: 'جاري التحميل...',
    save: 'حفظ',
    cancel: 'إلغاء',
    delete: 'حذف',
    edit: 'تعديل',
    create: 'إنشاء',
    search: 'بحث',
    filter: 'تصفية',
    export: 'تصدير',
    actions: 'الإجراءات',
    status: 'الحالة',
    view: 'عرض',
    confirm: 'تأكيد',
    back: 'رجوع',
    next: 'التالي',
    submit: 'إرسال',
    yes: 'نعم',
    no: 'لا',
    
    // Auth
    login: 'تسجيل الدخول',
    logout: 'تسجيل الخروج',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    forgot_password: 'نسيت كلمة المرور؟',
    reset_password: 'إعادة تعيين كلمة المرور',
    change_password: 'تغيير كلمة المرور',
    current_password: 'كلمة المرور الحالية',
    new_password: 'كلمة المرور الجديدة',
    confirm_password: 'تأكيد كلمة المرور',
    identifier_placeholder: 'البريد الإلكتروني أو الهوية الوطنية أو الإقامة',
    login_welcome: 'مرحباً بك في الشلال للموارد البشرية',
    login_subtitle: 'سجل دخولك للوصول إلى بوابة الموارد البشرية',
    first_login_notice: 'يرجى تغيير كلمة المرور للمتابعة',
    
    // Navigation
    dashboard: 'لوحة التحكم',
    employees: 'الموظفين',
    users: 'المستخدمين',
    documents: 'المستندات',
    leave: 'الإجازات',
    requests: 'الطلبات',
    reports: 'التقارير',
    settings: 'الإعدادات',
    audit_logs: 'سجلات المراجعة',
    system: 'النظام',
    profile: 'الملف الشخصي',
    
    // Roles
    role_employee: 'موظف',
    role_manager: 'مدير',
    role_hr_assistant: 'مساعد الموارد البشرية',
    role_hr_manager: 'مدير الموارد البشرية',
    role_hr_admin: 'مسؤول الموارد البشرية',
    role_it_inf: 'تقنية المعلومات والبنية التحتية',
    role_super_admin: 'المسؤول الأعلى',
    
    // Dashboard
    welcome_back: 'مرحباً بعودتك',
    signed_out_idle: 'تم إنهاء الجلسة لعدم النشاط. الرجاء تسجيل الدخول مرة أخرى.',
    signed_out_revoked: 'تم إنهاء هذه الجلسة. الرجاء تسجيل الدخول مرة أخرى.',
    signed_out_locked: 'هذا الحساب مقفل. الرجاء التواصل مع الموارد البشرية.',
    signed_out_inactive: 'تم تعطيل هذا الحساب. الرجاء التواصل مع الموارد البشرية.',
    quick_actions: 'إجراءات سريعة',
    recent_activity: 'النشاط الأخير',
    pending_approvals: 'الموافقات المعلقة',
    no_pending_items: 'لا توجد عناصر معلقة',
    
    // Audit
    audit_action: 'الإجراء',
    audit_actor: 'نفذ بواسطة',
    audit_target: 'الهدف',
    audit_timestamp: 'الوقت',
    audit_reason: 'السبب',
    audit_details: 'التفاصيل',
    
    // Users
    user_management: 'إدارة المستخدمين',
    create_user: 'إنشاء مستخدم',
    user_details: 'تفاصيل المستخدم',
    first_name: 'الاسم الأول',
    last_name: 'اسم العائلة',
    national_id: 'الهوية الوطنية',
    iqama: 'الإقامة',
    role: 'الدور',
    active: 'نشط',
    inactive: 'غير نشط',
    
    // System
    system_health: 'صحة النظام',
    diagnostics: 'التشخيصات',
    email_status: 'حالة البريد',
    database_status: 'حالة قاعدة البيانات',
    
    // Errors
    error_occurred: 'حدث خطأ',
    error_auth_required: 'المصادقة مطلوبة',
    error_access_denied: 'الوصول مرفوض',
    error_not_found: 'غير موجود',
    error_validation: 'خطأ في التحقق',
    
    // Success messages
    success_saved: 'تم الحفظ بنجاح',
    success_deleted: 'تم الحذف بنجاح',
    success_updated: 'تم التحديث بنجاح',
    
    // Empty states
    no_data: 'لا توجد بيانات',
    no_results: 'لا توجد نتائج',
    get_started: 'ابدأ الآن',
    
    // Import (Phase 13)
    bulkEmployeeImport: 'استيراد الموظفين بالجملة',
    importDescription: 'استيراد الموظفين من ملفات Excel أو CSV',
    uploadImport: 'تحميل واستيراد',
    importHistory: 'سجل الاستيراد',
    approvalsAudit: 'سجل الموافقات',
    approvalsAuditHint: 'من وافق على أي ملف (عبر SHA-256)، ومتى، وهل تم تنفيذه.',
    bulkSalary: 'الرواتب بالجملة',
    bulkSalaryLoader: 'محمّل الراتب الأساسي بالجملة',
    bulkSalaryLoaderHint: 'يقرأ فقط أعمدة الهوية (الإقامة / الهوية الوطنية / الرقم الوظيفي) وعمود الراتب، ويكتب الراتب الأساسي فقط. أولوية المطابقة: الإقامة ← الهوية الوطنية ← الرقم الوظيفي. القيمة الفارغة/صفر تُضبط من الملف؛ أي راتب مخزّن مختلف يُعلَّم كتعارض ولا يُستبدل أبداً.',
    salaryReasonNote: 'يُسجَّل كل تعديل للراتب مع سبب الحقل المحمي:',
    salarySelectFile: 'اختر ملف CSV/XLSX يحتوي على أعمدة الهوية وعمود الراتب.',
    salaryRunDryRun: 'تشغيل المعاينة',
    salaryDryRunFailed: 'فشلت المعاينة.',
    salaryExecuteFailed: 'فشل التنفيذ.',
    salaryApprovalRequired: 'شغّل معاينة تحتوي على صف واحد على الأقل للضبط للموافقة على هذا الملف قبل التنفيذ.',
    salaryExecuted: 'تم التنفيذ.',
    salaryDryRunReport: 'معاينة (لا تُكتب أي تغييرات)',
    salaryWillSet: 'سيتم الضبط',
    salaryNoChange: 'بدون تغيير',
    salaryConflict: 'تعارض',
    salarySkip: 'تم التخطي',
    salaryNotFound: 'غير موجود',
    salaryFailed: 'فشل',
    salaryReady: 'جاهز للتنفيذ — تم ربط رمز موافقة بهذا الملف.',
    salaryNotReady: 'لا يوجد ما يتم ضبطه (لا رواتب فارغة/صفرية مطابقة). التنفيذ غير مُفعّل.',
    salaryCurrent: 'الحالي',
    salaryFromFile: 'من الملف',
    salaryExecute: 'تنفيذ تحميل الرواتب',
    salaryLoadHistory: 'سجل تحميل الرواتب',
    salaryLoadHistoryHint: 'من حمّل أي ملف (عبر SHA-256)، ومتى، والحالة. نزّل ورقة الاعتماد الخالية من الأرقام.',
    noSalaryBatches: 'لا توجد عمليات تحميل رواتب بعد. تُسجّل المعاينة دفعة هنا.',
    matchedBy: 'تمت المطابقة عبر',
    uploadedBy: 'بواسطة',
    noApprovals: 'لا توجد موافقات بعد. تُسجَّل الموافقة هنا عند نجاح المحاكاة.',
    fileSha: 'بصمة الملف SHA-256',
    approvedBy: 'تمت الموافقة بواسطة',
    approvedAt: 'تاريخ الموافقة',
    executedBy: 'تم التنفيذ بواسطة',
    executed: 'تم التنفيذ',
    pendingExecution: 'قيد الانتظار',
    approvalRequired: 'قم بإجراء محاكاة ناجحة للموافقة على هذا الملف قبل التنفيذ.',
    signoffSheet: 'ورقة الاعتماد',
    signoffDownloadFailed: 'تعذّر تنزيل ورقة الاعتماد.',
    uploadFile: 'تحميل الملف',
    supportedFormats: 'الصيغ المدعومة: xlsx., csv.',
    importProfile: 'ملف تعريف الاستيراد',
    selectProfile: 'اختر الملف التعريفي',
    selectFile: 'اختر الملف',
    selectSheet: 'اختر الورقة',
    sheetHint: 'غيّر الورقة وأعد التحقق لاستخدام بيانات مختلفة',
    validating: 'جاري التحقق...',
    validateFile: 'تحقق من الملف',
    validationResult: 'نتيجة التحقق',
    uploadToValidate: 'قم بتحميل ملف لرؤية نتائج التحقق',
    importComplete: 'تم الاستيراد بنجاح!',
    created: 'تم إنشاؤه',
    updated: 'تم تحديثه',
    noChange: 'بدون تغيير',
    failed: 'فشل',
    downloadCredentials: 'تحميل بيانات الاعتماد CSV',
    totalRows: 'إجمالي الصفوف',
    total: 'الإجمالي',
    warnings: 'تنبيهات',
    rowsKept: 'تم الإبقاء على الصفوف ولم تُرفض',
    note: 'ملاحظة',
    runDryRun: 'تشغيل المعاينة التجريبية',
    runningDryRun: 'جارٍ التشغيل التجريبي...',
    dryRunHint: 'عاين نتيجة المطابقة/التحديث أدناه. لا تتم كتابة أي بيانات حتى تضغط تنفيذ.',
    dryRunPreview: 'معاينة تجريبية',
    noDataWritten: 'لا تتم كتابة بيانات',
    upsertSummary: 'أولوية المطابقة: الإقامة ← الهوية الوطنية ← الرقم الوظيفي. تُكتب الحقول غير الهوية فوق القديمة؛ وتُكتب حقول الهوية فقط عبر مفتاح المطابقة. غير تكراري، بلا حذف.',
    willCreate: 'إنشاء',
    willUpdate: 'تحديث',
    willFail: 'فشل',
    blocked: 'محظور',
    readyToExecute: 'لا توجد مشكلات حاجبة — جاهز للتنفيذ.',
    blockedFromExecute: 'يجب حل المشكلات الحاجبة قبل التنفيذ (انظر الأعلام أدناه).',
    flagDetail: 'تفاصيل الأعلام',
    noValuesShown: 'الصف والحقل فقط، بدون قيم',
    severity: 'الخطورة',
    type: 'النوع',
    resolveBlocking: 'حل الأعلام الحاجبة، أعد التحقق، ثم أعد تشغيل المعاينة قبل التنفيذ.',
    valid: 'صالح',
    invalid: 'غير صالح',
    file: 'الملف',
    sheet: 'الورقة',
    blockingErrors: 'أخطاء حاجبة',
    rowErrors: 'أخطاء الصفوف',
    row: 'الصف',
    field: 'الحقل',
    error: 'خطأ',
    executing: 'جاري التنفيذ...',
    executeImport: 'تنفيذ الاستيراد',
    fixErrorsFirst: 'أصلح أخطاء التحقق قبل التنفيذ',
    refresh: 'تحديث',
    noImportHistory: 'لا يوجد سجل استيراد بعد',
    rows: 'الصفوف',
    result: 'النتيجة',
    date: 'التاريخ',
    clickToUpload: 'انقر للتحميل أو اسحب وأفلت',
    noValidationId: 'لا يوجد معرف تحقق - يرجى التحقق أولاً',
    pleaseSelectFileAndProfile: 'يرجى اختيار ملف وملف تعريف',
  }
};

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    return localStorage.getItem('hrm_language') || 'en';
  });

  const isRTL = language === 'ar';

  // Update document direction and language
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    localStorage.setItem('hrm_language', language);
  }, [language, isRTL]);

  // Translation function. Honors an optional 2nd-arg default so components can
  // pass an inline English fallback: t('myKey', 'My Label').
  const t = useCallback((key, fallback) => {
    return translations[language]?.[key] || translations['en']?.[key] || fallback || key;
  }, [language]);

  // Get role display name
  const getRoleDisplay = useCallback((role) => {
    const roleKey = `role_${role}`;
    return t(roleKey);
  }, [t]);

  // Toggle language
  const toggleLanguage = useCallback(() => {
    setLanguageState(prev => prev === 'en' ? 'ar' : 'en');
  }, []);

  // Set specific language
  const setLanguage = useCallback((lang) => {
    if (lang === 'en' || lang === 'ar') {
      setLanguageState(lang);
    }
  }, []);

  const value = {
    language,
    isRTL,
    t,
    getRoleDisplay,
    toggleLanguage,
    setLanguage,
    translations: translations[language]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
